import {
  Box,
  TextField,
  Button,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Pagination,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { requestID, baseURL, config } from "../../../common/utils";
import { IonPage } from "@ionic/react";

export const fetchCategories = async (): Promise<string[]> => {
  const payload = { requestID: requestID() };
  const res = await axios.post(`${baseURL}warehouses/getCategories`, payload, config);
  
  if (res.data.status === "true") {
    return res.data.data.map((cat: any) => cat.category_name || cat.name); 
  }
  throw new Error(res.data.message || "Failed to fetch categories");
};

     const Category: React.FC = () => {
  
  const theme = useTheme();
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [category, setCategory] = useState<any[]>([]);
  const [loading, setLoading] = useState({
    table: false,
    submit: false,
    delete: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formValues, setFormValues] = useState({ id: "", name: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;

  const fetchCategory = async () => {
    try {
      setLoading(prev => ({...prev, table: true}));
      const result = await fetchCategories();
      setCategory(result.map((name, index) => ({ id: index.toString(), name })));
    } catch (error) {
      setError("Error fetching categories");
    } finally {
      setLoading(prev => ({...prev, table: false}));
    }
  };

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCategory(null);
    setFormValues({ id: "", name: "" });
    setError(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const payload = {
      requestID: requestID(),
      ...formValues,
      category_id: selectedCategory?.id,
    };

    try {
      setLoading(prev => ({...prev, submit: true}));
      setError(null);

      const endpoint = selectedCategory
        ? `${baseURL}categorys/editCategory`
        : `${baseURL}categorys/addCategory`;
      const res = await axios.post(endpoint, payload, config);

      if (res.data.status === "true") {
        setSuccess(selectedCategory ? "Category updated!" : "Category added!");
        fetchCategory();
        handleCloseDialog();
      } else {
        setError(res.data.message || "Operation failed");
      }
    } catch (err) {
      setError("Something went wrong");
    } finally {
      setLoading(prev => ({...prev, submit: false}));
    }
  };

  const handleEdit = (category: any) => {
    setSelectedCategory(category);
    setFormValues({ id: category.id, name: category.name });
    setOpenDialog(true);
  };

  const handleDelete = async (category_id: string) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;

    try {
      setLoading(prev => ({...prev, delete: true}));
      const payload = { requestID: requestID(), storeId: category_id };
      await axios.post(`${baseURL}categorys/deleteCategory`, payload, config);
      setSuccess("Category deleted successfully!");
      fetchCategory();
    } catch (error) {
      setError("Failed to delete category");
    } finally {
      setLoading(prev => ({...prev, delete: false}));
    }
  };

  useEffect(() => {
    fetchCategory();
  }, []);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const filteredCategory = category.filter((cat) =>
    cat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedCategory = filteredCategory.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
        <IonPage>
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4">Category Management</Typography>
        <Button onClick={handleOpenDialog} variant="contained" startIcon={<AddIcon />}>
          Add Category
        </Button>
      </Box>

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          action={
            <IconButton size="small" onClick={() => setSuccess(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {success}
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <IconButton size="small" onClick={() => setError(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Search Category"
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setPage(1);
        }}
        InputProps={{
          startAdornment: <SearchIcon sx={{ color: "action.active", mr: 1 }} />,
        }}
        sx={{ mb: 3 }}
        name="search"
      />

      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: theme.palette.grey[100] }}>
              <TableRow>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">#</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Category Name</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Actions</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading.table ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedCategory.length > 0 ? (
                paginatedCategory.map((cat, index) => (
                  <TableRow key={cat.id} hover>
                    <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{cat.name}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Edit">
                          <IconButton color="primary" onClick={() => handleEdit(cat)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(cat.id)}
                            disabled={loading.delete}
                          >
                            {loading.delete ? <CircularProgress size={24} /> : <DeleteIcon />}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography>{searchTerm ? "No matching category found" : "No categories available"}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {filteredCategory.length > 0 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={Math.ceil(filteredCategory.length / rowsPerPage)}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>{selectedCategory ? "Edit Category" : "Add New Category"}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Category Name"
            name="name"
            value={formValues.name}
            onChange={handleInputChange}
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">Cancel</Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={loading.submit || !formValues.name}
            startIcon={loading.submit ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {selectedCategory ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>      
      </IonPage>

  );
};
export default Category;
